import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';

// assets
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { useConfigMonitor } from 'Contextos/ConfigMonitorContext';

// Helper para formatear fechas amigables
const formatTimestamp = (isoString) => {
    if (!isoString) return 'Sin sincronizar';
    try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return 'Sin sincronizar';

        const now = new Date();
        const isToday =
            date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear();

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        if (isToday) {
            return `Hoy ${hours}:${minutes}`;
        }

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}/${month} ${hours}:${minutes}`;
    } catch {
        return 'Sin sincronizar';
    }
};

const ConfigStatusDialog = ({ open, onClose }) => {
    const theme = useTheme();

    const {
        catalogList,
        itemStates,
        summary,
        isSyncingAll,
        syncItem,
        syncAll,
        retryFailed,
        reconcileLocalState
    } = useConfigMonitor();

    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('ALL'); // ALL | CRITICAL | ERROR | LOADING | READY

    const sucursalActual = JSON.parse(localStorage.getItem('nombreSucursal')) || 'Sucursal activa';
    const ambienteActual = JSON.parse(localStorage.getItem('ambiente')) || '';

    // Reconciliación ligera cada vez que el diálogo se abre
    useEffect(() => {
        if (open) {
            reconcileLocalState();
        }
    }, [open, reconcileLocalState]);

    // Filtrar catálogo según término de búsqueda y categoría
    const filteredCatalog = useMemo(() => {
        return catalogList.filter((item) => {
            const state = itemStates[item.key] || { status: 'PENDING', recordCount: 0 };

            // Filtro por categoría
            if (filterCategory === 'CRITICAL' && !item.isCritical) return false;
            if (filterCategory === 'ERROR' && state.status !== 'ERROR') return false;
            if (filterCategory === 'LOADING' && state.status !== 'LOADING') return false;
            if (filterCategory === 'READY' && state.status !== 'READY') return false;

            // Filtro por búsqueda
            if (searchTerm.trim() !== '') {
                const term = searchTerm.toLowerCase();
                const matchLabel = item.label.toLowerCase().includes(term);
                const matchService = item.service.toLowerCase().includes(term);
                const matchStorage = item.storageType.toLowerCase().includes(term);
                const matchModules = (item.modules || '').toLowerCase().includes(term);
                return matchLabel || matchService || matchStorage || matchModules;
            }

            return true;
        });
    }, [catalogList, itemStates, filterCategory, searchTerm]);

    const handleRetryItem = (key) => {
        syncItem(key, { forceRefresh: false, retrying: true });
    };

    const handleRefreshItem = (key) => {
        syncItem(key, { forceRefresh: true });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Header del Diálogo */}
            <DialogTitle
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : '#f8fafc'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <StorageIcon sx={{ color: theme.palette.primary.main, fontSize: '1.75rem' }} />
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            Estado de Configuración Local
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {sucursalActual} {ambienteActual ? `(${ambienteActual})` : ''} · Datos locales en caché
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 1.5, sm: 3 }, bgcolor: theme.palette.background.default }}>
                {/* 1. Barra de Contadores y Resumen */}
                <Grid container spacing={1.5} sx={{ mb: 2, mt: 0.1 }}>
                    <Grid item xs={6} sm={2.4}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.25,
                                textAlign: 'center',
                                borderRadius: '10px',
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fff'
                            }}
                        >
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                                Total
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                                {summary.total}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} sm={2.4}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.25,
                                textAlign: 'center',
                                borderRadius: '10px',
                                borderColor: 'rgba(76, 175, 80, 0.4)',
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.05)'
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                ✓ Cargadas
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                                {summary.ready}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={4} sm={2.4}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.25,
                                textAlign: 'center',
                                borderRadius: '10px',
                                borderColor: 'rgba(33, 150, 243, 0.4)',
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.08)' : 'rgba(33, 150, 243, 0.05)'
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#0288d1' }}>
                                ⟳ Cargando
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0288d1' }}>
                                {summary.loading}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={4} sm={2.4}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.25,
                                textAlign: 'center',
                                borderRadius: '10px',
                                borderColor: summary.error > 0 ? 'rgba(244, 67, 54, 0.5)' : 'rgba(0,0,0,0.1)',
                                bgcolor: summary.error > 0 ? (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)') : 'transparent'
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, color: summary.error > 0 ? '#d32f2f' : 'inherit' }}>
                                ✕ Con error
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: summary.error > 0 ? '#d32f2f' : 'inherit' }}>
                                {summary.error}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={4} sm={2.4}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.25,
                                textAlign: 'center',
                                borderRadius: '10px',
                                borderColor: 'rgba(255, 152, 0, 0.4)',
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.08)' : 'rgba(255, 152, 0, 0.05)'
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                                ⏳ Pendientes
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#ed6c02' }}>
                                {summary.pending}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {/* 2. Barra de Acciones Globales y Filtros */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1.5,
                        mb: 2
                    }}
                >
                    {/* Botones de acción global */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            disabled={isSyncingAll || (summary.error === 0 && summary.pending === 0)}
                            onClick={retryFailed}
                            startIcon={isSyncingAll ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                        >
                            Reintentar fallidas {summary.error > 0 ? `(${summary.error})` : ''}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            disabled={isSyncingAll}
                            onClick={() => syncAll({ forceRefresh: true })}
                            startIcon={isSyncingAll ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                        >
                            Actualizar todas desde BD
                        </Button>
                    </Box>

                    {/* Buscador */}
                    <TextField
                        size="small"
                        placeholder="Buscar configuración..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 240 } }}
                    />
                </Box>

                {/* Filtros rápidos por Chip */}
                <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                        label={`Todas (${catalogList.length})`}
                        size="small"
                        clickable
                        color={filterCategory === 'ALL' ? 'primary' : 'default'}
                        variant={filterCategory === 'ALL' ? 'filled' : 'outlined'}
                        onClick={() => setFilterCategory('ALL')}
                    />
                    <Chip
                        label="Críticas (12)"
                        size="small"
                        clickable
                        color={filterCategory === 'CRITICAL' ? 'primary' : 'default'}
                        variant={filterCategory === 'CRITICAL' ? 'filled' : 'outlined'}
                        onClick={() => setFilterCategory('CRITICAL')}
                    />
                    {summary.error > 0 && (
                        <Chip
                            label={`Con error (${summary.error})`}
                            size="small"
                            clickable
                            color="error"
                            variant={filterCategory === 'ERROR' ? 'filled' : 'outlined'}
                            onClick={() => setFilterCategory('ERROR')}
                        />
                    )}
                    {summary.loading > 0 && (
                        <Chip
                            label={`Cargando (${summary.loading})`}
                            size="small"
                            clickable
                            color="info"
                            variant={filterCategory === 'LOADING' ? 'filled' : 'outlined'}
                            onClick={() => setFilterCategory('LOADING')}
                        />
                    )}
                    <Chip
                        label={`Cargadas (${summary.ready})`}
                        size="small"
                        clickable
                        color={filterCategory === 'READY' ? 'success' : 'default'}
                        variant={filterCategory === 'READY' ? 'filled' : 'outlined'}
                        onClick={() => setFilterCategory('READY')}
                    />
                </Box>

                {/* 3. Tabla de Configuraciones */}
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                        borderRadius: '12px',
                        maxHeight: 460,
                        overflowY: 'auto'
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: theme.palette.mode === 'dark' ? '#1a223f' : '#f1f5f9' } }}>
                                <TableCell>Configuración</TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Almacenamiento</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="center">Registros</TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Última Carga</TableCell>
                                <TableCell align="right">Acción</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCatalog.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            No se encontraron configuraciones para el filtro seleccionado.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredCatalog.map((item) => {
                                    const state = itemStates[item.key] || {
                                        status: 'PENDING',
                                        recordCount: 0,
                                        lastSuccessAt: null,
                                        error: null
                                    };
                                    const isLoading = state.status === 'LOADING';
                                    const isError = state.status === 'ERROR';
                                    const isReady = state.status === 'READY';
                                    const isPending = state.status === 'PENDING';

                                    return (
                                        <TableRow
                                            key={item.key}
                                            hover
                                            sx={{
                                                bgcolor: isError ? (theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.05)' : 'rgba(244, 67, 54, 0.03)') : 'inherit'
                                            }}
                                        >
                                            {/* Nombre + Badges */}
                                            <TableCell sx={{ py: 1.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {item.label}
                                                    </Typography>
                                                    {item.isCritical && (
                                                        <Chip
                                                            label="Requerido"
                                                            size="small"
                                                            color="primary"
                                                            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                                                        />
                                                    )}
                                                </Box>
                                                {item.modules && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: theme.palette.text.secondary, display: 'block', fontSize: '0.72rem' }}
                                                    >
                                                        {item.modules}
                                                    </Typography>
                                                )}
                                                {/* Detalle amigable si hubo error pero se conservan datos locales */}
                                                {isError && (
                                                    <Box sx={{ mt: 0.5 }}>
                                                        <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 600, display: 'block' }}>
                                                            {state.error || 'Error al consultar el servicio.'}
                                                        </Typography>
                                                        {state.recordCount > 0 && (
                                                            <Typography variant="caption" sx={{ color: theme.palette.success.dark, display: 'block', fontWeight: 500 }}>
                                                                ✓ Datos locales conservados ({state.recordCount} registros disponibles)
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>

                                            {/* Tipo de almacenamiento */}
                                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                                <Chip
                                                    label={item.storageType}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        borderColor: item.storageType === 'IndexedDB' ? '#9c27b0' : '#009688',
                                                        color: item.storageType === 'IndexedDB' ? '#9c27b0' : '#009688'
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Estado Visual */}
                                            <TableCell>
                                                {isLoading && (
                                                    <Chip
                                                        icon={<CircularProgress size={12} color="inherit" />}
                                                        label="Cargando..."
                                                        size="small"
                                                        color="info"
                                                        sx={{ height: 22, fontSize: '0.75rem' }}
                                                    />
                                                )}
                                                {isReady && (
                                                    <Chip
                                                        icon={<CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
                                                        label="Cargado"
                                                        size="small"
                                                        color="success"
                                                        sx={{ height: 22, fontSize: '0.75rem' }}
                                                    />
                                                )}
                                                {isError && (
                                                    <Chip
                                                        icon={<ErrorOutlineIcon style={{ fontSize: 14 }} />}
                                                        label="Error"
                                                        size="small"
                                                        color="error"
                                                        sx={{ height: 22, fontSize: '0.75rem' }}
                                                    />
                                                )}
                                                {isPending && (
                                                    <Chip
                                                        icon={<HourglassEmptyIcon style={{ fontSize: 14 }} />}
                                                        label="Pendiente"
                                                        size="small"
                                                        sx={{ height: 22, fontSize: '0.75rem' }}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Conteo de Registros */}
                                            <TableCell align="center">
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                                    {state.recordCount > 0
                                                        ? `${state.recordCount} ${state.recordCount === 1 ? 'reg.' : 'regs.'}`
                                                        : isReady
                                                            ? '1 reg.'
                                                            : '-'}
                                                </Typography>
                                            </TableCell>

                                            {/* Última Carga / Sincronización */}
                                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                    {formatTimestamp(state.lastSuccessAt)}
                                                </Typography>
                                            </TableCell>

                                            {/* Botón de Acción Individual */}
                                            <TableCell align="right">
                                                {isLoading ? (
                                                    <Button size="small" disabled sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                                                        Procesando...
                                                    </Button>
                                                ) : isError || isPending ? (
                                                    <Button
                                                        variant="outlined"
                                                        color="warning"
                                                        size="small"
                                                        onClick={() => handleRetryItem(item.key)}
                                                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1 }}
                                                    >
                                                        Reintentar
                                                    </Button>
                                                ) : (
                                                    <Tooltip title="Consultar base de datos para refrescar la copia local">
                                                        <Button
                                                            variant="text"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleRefreshItem(item.key)}
                                                            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                                                            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1 }}
                                                        >
                                                            Actualizar
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* 4. Nota explicativa sobre Caché y Actualización de BD */}
                <Box
                    sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                        border: `1px solid ${theme.palette.divider}`
                    }}
                >
                    <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.primary.main, mt: 0.2 }} />
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.4 }}>
                        <strong>Información sobre el almacenamiento:</strong> «Cargado» indica que los registros se encuentran
                        disponibles localmente en el navegador para que los módulos funcionen con máxima rapidez. Al presionar{' '}
                        <strong>Actualizar</strong>, el sistema consulta directamente la base de datos y reemplaza el caché local únicamente
                        si la consulta finaliza de manera exitosa.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ConfigStatusDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

export default ConfigStatusDialog;
